import React, { useEffect, useRef, useState } from 'react';
import { isPlatform } from '@ionic/react';
import { NoInternetConnection, useDeepUpdateEffect } from '@mindly/ui-components';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { useSelector } from 'react-redux';

import { setStateIos } from '../../redux/isIos/reducer';
import { loadUserThunk } from 'src/redux/user/thunk';
import { userSelector } from '../../redux/user/selector';
import { loadConsultationsThunk } from '../../redux/consultations/action';
import { setStatus } from '../../redux/internetConnection/reducer';
import { internetConnectionSelector } from '../../redux/internetConnection/selector';
import { getFirestoreGlobalVariablesThunk } from '../../redux/firestoreGlobalVariables/action';
import { useAppDispatch } from '../../redux/hooks';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import '../../theme/variables.css';
import './index.css';
import { setIsInitialLoading } from '../../redux/initialLoading/reducer';
import AppLoader from '../AppLoader';
import { setIsActive } from '../../redux/tabBar/reducer';
import { setIsActive as setIsActiveSettings } from '../../redux/settingsModal/reducer';

import { ReviewTrialSessionFeature } from '../../features/ReviewTrialSessionFeature';
import { ReviewSubscriptionSessionFeature } from '../../features/ReviewSubscriptionSessionFeature';
import 'react-image-crop/dist/ReactCrop.css';
import 'cropperjs/dist/cropper.css';
import AddAvatarModal from '../../modals/AddAvatarModal';
import Routing from '../Routing';
import { useIntl } from 'react-intl';
import NeedUpdateModal from '../../modals/NeedUpdateModal/NeedUpdateModal';
import { DateTime } from 'luxon';
import { changeUserData } from '../../api/user';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import useLangs from '../../context/LangsContext';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import SettingsModal from '../../modals/SettingsModal';
import { isSettingsModalActive } from '../../redux/settingsModal/selector';
import { avatarModalStatusSelector } from '../../redux/avatarModal/selector';
import { setAvatarModalStatus } from 'src/redux/avatarModal/reducer';
import { useHistory } from 'react-router';
import { PrivateRoutes, PublicRoutes } from 'src/routes/navigation';
import SubscriptionModal from '../../modals/SubscriptionModal';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import AppUrlListener from '../AppUrlListener/AppUrlListener';
import { useRemoteConfig } from '../../hooks/useRemoteConfig/useRemoteConfig';
import {
  useNeedUpdatePersonalData,
  useTrialSessionReviewTimer,
  useSubscriptionSessionReviewTimer,
  useUpdateSessionsTime,
  useAnalytics,
  useLog,
} from '../../shared';
import { fetchPromoCodes } from '../../redux/promoCodes/promoCodes.thunk';
import { fetchRatedConsultations } from '../../redux/consultationReviews/consultationsReviews.thunk';
import { usePushNotifications } from '../../shared/context/PushNotificationsContext';
import { uid } from 'uid';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { useAppUpdatesNotSupported } from '../../shared/hooks/useAppUpdatesNotSupported';
import { TranslatedAppNotSupportedFeature } from '../../translated-components';
import BuildInformation from '../BuildInformation/BuildInformation';
import { initChatRooms } from 'src/redux/chat/thunk';
import { ChatControlsWidget } from 'src/widgets';
import { setSplashColor } from 'src/shared/utils/splashColor';
import { Style } from '@capacitor/status-bar';
import { SupportButton } from '../SupportButton';
import { clearSavedSort } from 'src/shared/utils/filters';
import { updateUsedDevices } from 'src/api/usedDevices';
import { logDeviceInfo } from 'src/shared/utils/logDeviceInfo';
import { getDefaultOnBoardingFlowKey } from 'src/api/onBoardingFlow';
import { setOnBoardingKey } from 'src/redux/onBoardingFlow/reducer';
import { closeAllSessions } from 'src/shared/utils/manageTrialSessionReview';
import { useUpdateBrowserAndClickId } from 'src/shared/hooks/useUpdateBrowserAndClickId';
import { MindlyReviewFeature } from '../../features/MindlyReviewFeature';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';
import { useAuthFlow } from 'src/shared';
import { ChangeLanguageModalWithLanguageWorkInProgressFeature } from 'src/features/ChangeLanguageModalWithLanguageWorkInProgressFeature';

const noLoaderRoutes = [PublicRoutes.PAYMENT_SUCCESS.replace('/:specialistID', '')];

const App: React.FC = () => {
  const intl = useIntl();
  const { remoteConfigInit } = useRemoteConfig();
  const { locale } = useLangs();
  const { bootPlugin, updateUser, showMessenger } = useIntercomPlugin();
  const history = useHistory();
  const { triggerSignOutFlow, isAuthLoading, authUser, isAuthReady, triggerCreateOrLinkUserWithRedirectResultFlow } =
    useAuthFlow();
  const isAvatarModalShowed = useRef(false);
  const pushNotificationStateCounter = useRef<number>(0);
  const isAppSupported = useAppUpdatesNotSupported();
  useSubscriptionSessionReviewTimer();
  useTrialSessionReviewTimer();
  useUpdateSessionsTime();
  useUpdateBrowserAndClickId();

  const dispatch = useAppDispatch();
  const currentUser = useSelector(userSelector);
  const internetConnection = useSelector(internetConnectionSelector);
  const isIos = useSelector((state) => state.isIos);
  const isSettingsActive = useSelector(isSettingsModalActive);
  const avatarModalStatus = useSelector(avatarModalStatusSelector);
  const lastSubscription = useSelector(lastActiveSubscriptionSelector());

  const [isLoading, setIsLoading] = useState(!noLoaderRoutes.some((path) => window.location.pathname.includes(path)));
  const [isOpenLanguagesModal, setIsOpenLanguagesModal] = useState(false);
  const [intercomBootStatus, setIntercomBootStatus] = useState(false);
  const [isSubscriptionModal, setIsSubscriptionModal] = useState(false);

  const { trackPredefinedEvent, identify } = useAnalytics();
  const { logInfo, setUser: setUserLog } = useLog();
  const pushNotificationState = usePushNotifications();
  const isUserReady = useRef(false);
  const growthbook = useGrowthBook();

  useNeedUpdatePersonalData();

  useEffect(() => {
    dispatch(setStateIos());

    closeAllSessions();
  }, []);

  useEffect(() => {
    setUserLog(authUser ?? null);

    if (!authUser?.uid) return;

    growthbook.updateAttributes({
      id: authUser?.uid,
      ...(authUser?.metadata?.creationTime && { creationTime: new Date(authUser.metadata.creationTime).toISOString() }),
    });
  }, [authUser, isAuthReady]);

  useEffect(() => {
    if (authUser?.uid) {
      identify(authUser?.uid, authUser?.email ? { $email: authUser?.email } : undefined);
    }
  }, [authUser?.uid, authUser?.email]);

  useEffect(() => {
    if (pushNotificationState.isOpenPushNotificationModal) {
      pushNotificationStateCounter.current += 1;
      if (pushNotificationStateCounter.current === 1) {
        trackPredefinedEvent('NOTIFICATION_PERMISSION_VIEWED');
      }
    }
  }, [pushNotificationState.isOpenPushNotificationModal]);

  useEffect(() => {
    Network.addListener('networkStatusChange', (status) => {
      dispatch(setStatus(status.connected));
    });
    const logCurrentNetworkStatus = async () => {
      const status = await Network.getStatus();
      dispatch(setStatus(status.connected));
    };
    logCurrentNetworkStatus();
  }, []);

  useEffect(() => {
    const callLoaderData = async () => {
      if (!isPlatform('hybrid')) return initialLoaderData();

      try {
        const nativeUpdateInfo = await AppUpdate.getAppUpdateInfo();
        if (nativeUpdateInfo.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
          initialLoaderData();
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        initialLoaderData();
      }
    };

    try {
      SplashScreen.hide();
    } catch (e) {
      console.error('SplashScreen crash!!!');
    }

    if (isAuthReady) {
      callLoaderData();
    }
  }, [isAuthReady]);

  useEffect(() => {
    if (pushNotificationState.isOpenPushNotificationModal) return;
    if (isAvatarModalShowed.current) return;

    if (
      !window.location.pathname.includes(PrivateRoutes.PSYCHOLOGIST_LIST) &&
      !window.location.pathname.includes(PrivateRoutes.CONSULTATION)
    ) {
      return;
    }

    if (!currentUser?.email) return;
    // we need to ask for avatar only after user has completed onboarding
    if (
      (!currentUser?.onBoardingAnswers || !Object.keys(currentUser?.onBoardingAnswers).length) &&
      !currentUser?.is_viewed_specialist_list
    )
      return;
    if (isLoading) return;
    if (currentUser?.id && !currentUser?.avatar && isAppSupported) {
      dispatch(setAvatarModalStatus(true));
      isAvatarModalShowed.current = true;
    }
  }, [
    currentUser?.id,
    currentUser?.avatar,
    currentUser?.onBoardingAnswers,
    isLoading,
    window.location.pathname,
    isAvatarModalShowed,
    isAppSupported,
    pushNotificationState.isOpenPushNotificationModal,
  ]);

  useEffect(() => {
    if (window.location.pathname.includes('on-boarding')) return;

    (async () => {
      if (isLoading || intercomBootStatus) return;

      await bootPlugin({
        languageOverride: locale ?? 'pl',
        userId: currentUser?.id ?? '',
        email: currentUser?.email ?? '',
        name: currentUser?.display_name ?? '',
        customAttributes: {},
      });

      setIntercomBootStatus(true);
    })();
  }, [isLoading, currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id && intercomBootStatus) {
      updateUser({
        userId: currentUser?.id,
        email: currentUser?.email,
        name:
          currentUser?.display_name && currentUser?.last_name
            ? `${currentUser?.display_name} ${currentUser?.last_name}`
            : currentUser?.display_name ?? '',
        languageOverride: locale ?? 'pl',
        customAttributes: {},
      });
    } else {
      updateUser({
        languageOverride: locale ?? 'pl',
        customAttributes: {},
      });
    }
  }, [
    currentUser?.id,
    currentUser?.email,
    currentUser?.display_name,
    currentUser?.app_lang,
    locale,
    intercomBootStatus,
  ]);

  useEffect(() => {
    if (isLoading) return;
    if (currentUser?.id) return;

    const token = new URLSearchParams(window.location.search).get('token');
    if (
      // don't show languages modal if user is signed in with token (after onboarding case)
      !token &&
      !currentUser?.id &&
      !localStorage.getItem('mindlySelectedLocale') &&
      !window.location.pathname.includes('on-boarding') &&
      !window.location.pathname.includes('flow') &&
      !window.location.pathname.includes('payment') &&
      !window.location.pathname.includes('/s/')
    ) {
      setIsOpenLanguagesModal(true);
    }
  }, [currentUser?.id, isLoading]);

  useEffect(() => {
    if (!currentUser?.id) return;

    changeUserData({ time_zone: DateTime.local().zoneName }, currentUser?.id);

    getClientOnBoardingFlowKey();

    (async () => {
      try {
        const deviceInfo = await logDeviceInfo();
        await updateUsedDevices(deviceInfo);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [currentUser?.id]);

  const initialLoaderData = async () => {
    if (!noLoaderRoutes.some((path) => window.location.pathname.includes(path))) setIsLoading(true);
    try {
      const localGlobalVars = await dispatch(getFirestoreGlobalVariablesThunk()).unwrap();
      await remoteConfigInit(localGlobalVars?.minimumFetchIntervalForRemoteConfigInSeconds);

      trackPredefinedEvent('AppOpened', {
        eventInstanceId: uid(16),
        ...(authUser?.uid && { userId: authUser?.uid }),
        ...(authUser?.email && { email: authUser?.email }),
      });

      /**
       * Because we use redirect method on web, we need to check if the user is new and create a new user
       * before we start loading initial information for the app, on native platforms we don't use redirect method
       * so we don't need to check if the user is new and create a new user
       */
      if (!isPlatform('hybrid')) {
        await triggerCreateOrLinkUserWithRedirectResultFlow();
      }

      if (!authUser) return;

      const reduxUser = await dispatch(loadUserThunk(authUser.uid)).unwrap();
      if (!reduxUser) {
        logInfo('[InitialLoaderData]: reduxUser is null', {
          user: {
            ...(authUser ?? {}),
            uid: `${authUser?.uid?.substring(0, 3)}...${authUser?.uid?.substring(authUser?.uid?.length - 4)}`,
            id: `${authUser?.uid?.substring(0, 3)}...${authUser?.uid?.substring(authUser?.uid?.length - 4)}`,
            email: `${authUser?.email ? authUser?.email?.substring(0, 3) : 'undefined'}`,
          },
          reduxUser,
        });

        await triggerSignOutFlow();
        return;
      }

      await dispatch(fetchSubscriptions(authUser.uid));

      await Promise.all([dispatch(fetchRatedConsultations(authUser.uid))]);

      const consultations = await dispatch(loadConsultationsThunk(authUser.uid));
      await dispatch(initChatRooms());

      dispatch(setIsInitialLoading(false));
      if (
        !window.location.pathname.includes('on-boarding') &&
        !window.location.pathname.includes('tabs/psychologist-profile') &&
        !window.location.pathname.match(/tabs\/sessions\/\w+/) &&
        !window.location.pathname.startsWith('/s/')
      ) {
        dispatch(setIsActive(true));
      }

      if (consultations?.length && (history.location.pathname === '/auth' || history.location.pathname === '/')) {
        history.push(PrivateRoutes.CONSULTATION);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!lastSubscription) return;

    const end = DateTime.fromSeconds(lastSubscription.period_end);
    const toDay = DateTime.now();

    const diffDays = end.diff(toDay, 'days').toObject().days ?? 0;

    const daysLeft = Math.ceil(diffDays);
    const availableSessions = lastSubscription.session_count - lastSubscription.used_balance;

    const storageData = sessionStorage.getItem('subscriptionModal');

    if (daysLeft <= 14 && availableSessions > 0 && !storageData && !lastSubscription.pause_collection) {
      setIsSubscriptionModal(true);
      sessionStorage.setItem('subscriptionModal', 'snowed');
    }
  }, [lastSubscription]);

  useEffect(() => {
    if (isLoading) return;
    setSplashColor(isSubscriptionModal ? '#177590' : '#fffffe', isSubscriptionModal ? Style.Dark : Style.Light);
  }, [isSubscriptionModal, isLoading]);

  useEffect(() => {
    dispatch(getFirestoreGlobalVariablesThunk());
  }, [currentUser?.id]);

  useDeepUpdateEffect(() => {
    if (!!currentUser?.onBoardingAnswers && !isUserReady.current) {
      isUserReady.current = true;
      return;
    }
    clearSavedSort();
  }, [currentUser?.onBoardingAnswers]);

  const getClientOnBoardingFlowKey = async () => {
    const { data: flowData } = await getDefaultOnBoardingFlowKey(locale);
    if (flowData?.onboarding_key) {
      dispatch(setOnBoardingKey(flowData.onboarding_key));
    }
  };

  useEffect(() => {
    if (currentUser?.email && currentUser?.id) {
      dispatch(
        fetchPromoCodes({
          client_id: currentUser?.id,
        })
      );
    }
  }, [currentUser?.email, currentUser?.id]);

  return isLoading || isAuthLoading ? (
    <AppLoader />
  ) : (
    <>
      <ChangeLanguageModalWithLanguageWorkInProgressFeature
        isOpen={isOpenLanguagesModal}
        closeModal={() => setIsOpenLanguagesModal(false)}
      />
      {isAppSupported ? <Routing /> : null}
      <AppUrlListener />
      {!internetConnection && (
        <NoInternetConnection
          isInternetConnected={internetConnection}
          isIos={isIos}
          heading={intl.formatMessage({
            id: 'app.no_internet_connection',
            defaultMessage: 'Інтернет підключення відсутнє',
          })}
        />
      )}
      <TranslatedAppNotSupportedFeature isOpen={!isAppSupported} showMessenger={showMessenger}>
        <div className="my-5">
          <BuildInformation />
        </div>
      </TranslatedAppNotSupportedFeature>
      <NeedUpdateModal />
      <AddAvatarModal isOpen={avatarModalStatus} onDidDismiss={() => dispatch(setAvatarModalStatus(false))} />

      {currentUser?.id && <ReviewTrialSessionFeature />}
      {currentUser?.id && <ReviewSubscriptionSessionFeature />}
      {currentUser?.id && currentUser?.email && <MindlyReviewFeature />}
      <SettingsModal isOpen={isSettingsActive} onClose={() => dispatch(setIsActiveSettings(false))} />
      <SubscriptionModal isOpen={isSubscriptionModal} onDidDismiss={() => setIsSubscriptionModal(false)} />
      <SupportButton />
      <ChatControlsWidget />
    </>
  );
};

export default App;
